import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';
import { useActiveTenantFeatureSubscriptionsContextProvider } from 'shared/providers/tenant-feature-subscriptions/context/active-tenant-feature-subscriptions-provider';

import { useConnectorsContextProvider } from 'features/connectors/context/connectors-provider';
import { ConnectorType } from 'features/connectors/domain/connector';
import { validateSingleVcareConnector } from 'features/connectors/domain/queries/connector-queries';

interface ConnectorValidationHook {
  validateSingularityOfConnector: (connectorType: ConnectorType) => Promise<boolean | string>;
  validateType: (value: ConnectorType) => Promise<boolean | string>;
  isConnectorTypeValid: boolean;
  currentFormType: ConnectorType;
}

const useConnectorValidation = (): ConnectorValidationHook => {
  const { t } = useTranslation('connectors');
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const { hasActiveCareConnectFeature } = useActiveTenantFeatureSubscriptionsContextProvider();

  const { watch } = useFormContext();
  const currentFormType: ConnectorType = watch('type');

  const { connectorDetailsHook } = useConnectorsContextProvider();
  const { isConnectorTypeValid, setIsConnectorTypeValid } = connectorDetailsHook;

  const validateSingularityOfConnector = async (connectorType: ConnectorType): Promise<boolean> => {
    if (!connectorType) return true;

    if (connectorType === ConnectorType.Vcare) {
      try {
        await validateSingleVcareConnector(authenticatedInstance, {});
        return true;
      } catch {
        return false;
      }
    }

    return true;
  };

  const validateType = async (value: ConnectorType): Promise<boolean | string> => {
    if (!value || value === ConnectorType.Unknown) {
      setIsConnectorTypeValid(false);
      return t('info.requiredHelperText');
    }

    if (value === ConnectorType.Vcare) {
      if (!hasActiveCareConnectFeature) {
        setIsConnectorTypeValid(false);
        return t('details.errors.notSubscribedConnectorType');
      }
    }

    if (!(await validateSingularityOfConnector(value))) {
      setIsConnectorTypeValid(false);
      return t('details.errors.oneConnectorTypeAllowed');
    }

    setIsConnectorTypeValid(true);
    return true;
  };

  return {
    validateSingularityOfConnector,
    validateType,
    isConnectorTypeValid,
    currentFormType,
  };
};

export default useConnectorValidation;
